import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { TABLES } from "../../../Apis/Tables";
import DropDownMenuItem from "../../../components/DropDown/DropDownMenuItem";
import DropDownMenuProfile from "../../../components/DropDown/DropDownMenuProfile";
import {
    IconMoreVerticalDots,
    IconQr,
    LinkIconsOptions
} from "../../../components/buttons/IconButtons";
import TableFunction from "../../../components/table/TableFunction";
import { SEARCH_MODEL } from "../../../components/table/Model/TableModel";
import { VIEW_TYPE } from "../../../enums/ViewType";
import { SimpleSearchWithId } from "../../../reduxToolkit/Search/SearchApi";
import { RESIDENT_STATUS, downloadResidentQr, setApartmentResidents } from "../../Building/Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Household members logic of my apartment detail: paginated search of the
 * members that share the apartment with the logged resident, per card actions
 * menu and the details and registration link modals opened from it.
 *
 * @param {Object} props
 * @param {string} props.buildingApartmentId - Apartment of the logged resident, the members belong to it.
 * @param {boolean} props.modified - Flag raised by the add member modal so the list is reloaded.
 * @param {Function} props.setModified - Lowers the flag once the list is loaded again.
 * @returns {Object} Localization function, list state and the card handlers.
 */
export const MyHouseholdMembersCardsApp = ({ buildingApartmentId, modified, setModified }) => {
    const dispatch = useDispatch();
    const option = TABLES.BUILDING_APARTMENT_RESIDENT_NO_OWNER;
    const [searchElement, setSearchElement] = useState("");
    const [results, setResults] = useState({ content: [], totalElements: 0 });
    const [detailsModal, setDetailsModal] = useState(false);
    const [invitationLinkModal, setInvitationLinkModal] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null);
    // Flags SearchSimple works with, the search itself runs through getData
    const [search, setSearch] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false);
    // Apartment the last fetch ran with, the detail is not loaded yet on mount
    const requestedApartmentId = useRef(null);
    // Scrollable container of the grid, panelTables-container sizes it with --top-value
    const elementRef = useRef(null);

    /**
     * Loads the household members page the grid renders, the members are searched
     * through the BUILDING_APARTMENT_RESIDENT_NO_OWNER model of the simple search
     * by id, which takes the apartment as the userId of the body and leaves the
     * owner of the unit out of the list.
     *
     * @param {number} page - Page requested by the pagination, zero based.
     * @param {Object} [options] - Extra values sent by TableFunction.
     * @param {number} [options.rowsPerPage] - Page size in use.
     * @param {string} [options.searchElement] - Search value the fetch runs with.
     */
    const getData = (page, options) => {
        // The apartment is required by the search, on the first render of the
        // detail it is still loading and the fetch is left to the effect below
        if (!buildingApartmentId) return;

        requestedApartmentId.current = buildingApartmentId;

        const body = SEARCH_MODEL.setObject({
            page,
            size: options?.rowsPerPage || rowsPerPage,
            model: option,
            key: options?.searchElement ?? searchElement,
            userId: buildingApartmentId
        });

        dispatch(SimpleSearchWithId({ body })).then(
            ({ payload: { data: { data, success } } }) => {
                if (success !== true) return;

                setResults({
                    ...data,
                    // The card paints one name, the relationship with the main
                    // resident and the chip of the verification status, the
                    // resident type of the member is not shown here
                    content: setApartmentResidents(data?.content).map((member) => ({
                        ...member,
                        status: member?.verificationStatus
                    }))
                });
            }
        );
    };

    // Use TableFunction to handle the grid logic (pagination, actions menu)
    const {
        t,
        pagination,
        rowsPerPage,
        openMenu,
        handleMenuToggle,
        handleClose
    } = TableFunction({
        data: results,
        labelRowsPerPage: 'members_per_page',
        hasContainer: true,
        fixedPagination: false,
        view: VIEW_TYPE.GRID.name,
        searchElement,
        getData,
        modified,
        setModified
    });

    // Loads the list once the apartment of the detail arrives, the mount fetch of
    // TableFunction is skipped while there is no apartment to search with
    useEffect(() => {
        if (!buildingApartmentId || requestedApartmentId.current === buildingApartmentId) return;

        setModified(true);
    }, [buildingApartmentId]);

    /**
     * Distance from the top of the viewport to the grid, panelTables-container
     * takes the rest of the screen with it and leaves the pagination outside
     * of the scroll.
     */
    useLayoutEffect(() => {
        if (!elementRef?.current) return;

        const distanceTop = elementRef.current.getBoundingClientRect().top + 90;
        elementRef.current.style.setProperty('--top-value', `${distanceTop}px`);
    }, [results?.content]);

    /**
     * Search of SearchSimple, the members need the apartment on the body so the
     * fetch is the same getData of the grid instead of the generic one.
     */
    const handleSearch = () => getData(0);

    /**
     * Reset of SearchSimple, brings the first page back without the search key.
     */
    const handleResetSearch = () => getData(0, { searchElement: "" });

    /**
     * Closes the details modal and clears the member it was showing.
     */
    const hideDetails = () => {
        setDetailsModal(false);
        setSelectedItem(null);
    };

    /**
     * Vertical three dots menu with the actions available for one member.
     *
     * @param {Object} item - Household member data.
     * @param {number} index - Card position, used for the menu item ids.
     * @returns {JSX.Element}
     */
    const getActionsButtons = (item, index) => {
        return (
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item?.id}`}
                icon={<IconMoreVerticalDots />}
            >
                {/* Only an unverified member still needs the registration link */}
                {item?.verificationStatus === RESIDENT_STATUS.UNVERIFIED &&
                    <DropDownMenuItem
                        id={`btn_hhm_registration_link_${item?.id}`}
                        index={index}
                        handleClose={handleClose}
                        name={'registration_link'}
                        icon={<LinkIconsOptions/>}
                        click={() => {
                            setSelectedItem(item);
                            setInvitationLinkModal(true);
                        }}
                    />
                }
                <DropDownMenuItem
                    id={`btn_hhm_qr_access_${item?.id}`}
                    index={index}
                    handleClose={handleClose}
                    name={'qr_access'}
                    icon={<IconQr/>}
                    click={() => downloadResidentQr(dispatch)}
                />
            </DropDownMenuProfile>
        );
    };

    return {
        t,
        option,
        searchElement,
        setSearchElement,
        results,
        setResults,
        rowsPerPage,
        search,
        setSearch,
        simpleSearch,
        setSimpleSearch,
        handleSearch,
        handleResetSearch,
        detailsModal,
        invitationLinkModal,
        setInvitationLinkModal,
        selectedItem,
        setSelectedItem,
        hideDetails,
        getActionsButtons,
        pagination,
        elementRef,
    };
};
